import React from 'react';
import { Users, MapPin, User, Play, Eye, Clock } from 'lucide-react';

const GameSetup = ({
    gameMode,
    setGameMode,
    numPlayers,
    setNumPlayers,
    numSpies,
    setNumSpies,
    gameTimer,
    setGameTimer,
    onStartGame,
    t,
    selectedHumanCategory,
    setSelectedHumanCategory
}) => {
    const maxSpies = Math.floor(numPlayers / 2);
    const spyProgress = maxSpies === 1 ? 100 : ((numSpies - 1) / (maxSpies - 1)) * 100;
    const sectionClass = 'border-t border-spy-border py-[18px]';
    const labelClass = 'mb-4 block text-[10.5px] font-semibold uppercase tracking-[0.22em] text-spy-primary';
    const stepperButtonClass = 'grid h-[38px] w-[38px] place-items-center rounded-full border border-spy-border2 bg-transparent text-lg font-bold text-spy-soft hover:border-spy-primary hover:text-spy-primary disabled:cursor-not-allowed disabled:opacity-35';
    const progressTrackClass = 'relative h-[7px] w-full overflow-hidden rounded-full bg-spy-bg2';
    const progressFillClass = 'absolute left-0 top-0 h-full rounded-full bg-spy-primary transition-all duration-500 ease-in-out';

    return (
        <div className="flex flex-1 flex-col">
            {/* Game Mode */}
            <section className={sectionClass}>
                <label className={labelClass}>
                    {t.gameMode}
                </label>
                <div className="grid grid-cols-2 gap-3">
                    <button
                        onClick={() => setGameMode('location')}
                        className={`flex min-h-[82px] flex-col items-center justify-center rounded-spy border p-4 ${gameMode === 'location'
                            ? 'border-spy-primary bg-spy-surface text-spy-primary'
                            : 'border-spy-border2 bg-transparent text-spy-muted hover:border-spy-primary hover:text-spy-soft'
                            }`}
                    >
                        <MapPin className="mb-2 h-5 w-5" />
                        <span className="text-center font-display text-[18px] font-medium leading-tight">{t.locationMode}</span>
                    </button>
                    <button
                        onClick={() => setGameMode('human')}
                        className={`flex min-h-[82px] flex-col items-center justify-center rounded-spy border p-4 ${gameMode === 'human'
                            ? 'border-spy-primary bg-spy-surface text-spy-primary'
                            : 'border-spy-border2 bg-transparent text-spy-muted hover:border-spy-primary hover:text-spy-soft'
                            }`}
                    >
                        <User className="mb-2 h-5 w-5" />
                        <span className="text-center font-display text-[18px] font-medium leading-tight">{t.humanMode}</span>
                    </button>
                </div>

                {gameMode === 'human' && (
                    <div className="mt-4">
                        <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-spy-muted">
                            {t.humanCategory}
                        </label>
                        <select
                            value={selectedHumanCategory}
                            onChange={(e) => setSelectedHumanCategory(e.target.value)}
                            className="w-full rounded-full border border-spy-border2 bg-spy-surface px-4 py-2.5 text-sm font-semibold text-spy-soft"
                        >
                            <option value="celebrity">{t.categories.celebrity}</option>
                            <option value="art">{t.categories.art}</option>
                            <option value="sports">{t.categories.sports}</option>
                            <option value="history">{t.categories.history}</option>
                            <option value="politics">{t.categories.politics}</option>
                            <option value="science">{t.categories.science}</option>
                            <option value="technology">{t.categories.technology}</option>
                        </select>
                    </div>
                )}
            </section>

            {/* Players */}
            <section className={sectionClass}>
                <label className={labelClass}>
                    {t.players}
                </label>
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => setNumPlayers(Math.max(3, numPlayers - 1))}
                        className={stepperButtonClass}
                        disabled={numPlayers <= 3}
                    >
                        -
                    </button>

                    <div className="flex-1 mx-4">
                        <div className="flex items-center justify-center space-x-3 mb-2">
                            <Users className="h-5 w-5 text-spy-muted" />
                            <span className="font-display text-[28px] font-semibold leading-none text-spy-text">{numPlayers}</span>
                        </div>
                        <div className={progressTrackClass}>
                            <div
                                className={progressFillClass}
                                style={{ width: `${((numPlayers - 3) / (10 - 3)) * 100}%` }}
                            ></div>
                        </div>
                        <div className="mt-1 flex justify-between text-xs text-spy-muted">
                            <span>3</span>
                            <span>10</span>
                        </div>
                    </div>

                    <button
                        onClick={() => setNumPlayers(Math.min(10, numPlayers + 1))}
                        className={stepperButtonClass}
                        disabled={numPlayers >= 10}
                    >
                        +
                    </button>
                </div>
            </section>

            {/* Spies */}
            <section className={sectionClass}>
                <label className={labelClass}>
                    {t.spies}
                </label>
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => setNumSpies(Math.max(1, numSpies - 1))}
                        className={stepperButtonClass}
                        disabled={numSpies <= 1}
                    >
                        -
                    </button>

                    <div className="flex-1 mx-4">
                        <div className="flex items-center justify-center space-x-3 mb-2">
                            <Eye className="h-5 w-5 text-spy-danger" />
                            <span className="font-display text-[28px] font-semibold leading-none text-spy-text">{numSpies}</span>
                        </div>
                        <div className={progressTrackClass}>
                            <div
                                className={progressFillClass}
                                style={{ width: `${spyProgress}%` }}
                            ></div>
                        </div>
                        <div className="mt-1 flex justify-between text-xs text-spy-muted">
                            <span>1</span>
                            <span>{maxSpies}</span>
                        </div>
                    </div>

                    <button
                        onClick={() => setNumSpies(Math.min(maxSpies, numSpies + 1))}
                        className={stepperButtonClass}
                        disabled={numSpies >= maxSpies}
                    >
                        +
                    </button>
                </div>
            </section>

            {/* Timer Selection */}
            <section className={sectionClass}>
                <label className={labelClass}>
                    {t.timer}
                </label>
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => setGameTimer(Math.max(5, gameTimer - 1))}
                        className={stepperButtonClass}
                        disabled={gameTimer <= 5}
                    >
                        -
                    </button>

                    <div className="flex-1 mx-4">
                        <div className="flex items-center justify-center space-x-3 mb-2">
                            <Clock className="h-5 w-5 text-spy-muted" />
                            <span className="font-display text-[28px] font-semibold leading-none text-spy-text">{gameTimer}</span>
                            <span className="text-sm text-spy-muted">{t.minutes}</span>
                        </div>
                        <div className={progressTrackClass}>
                            <div
                                className={progressFillClass}
                                style={{ width: `${((gameTimer - 5) / (15 - 5)) * 100}%` }}
                            ></div>
                        </div>
                        <div className="mt-1 flex justify-between text-xs text-spy-muted">
                            <span>5{t.minutes}</span>
                            <span>15{t.minutes}</span>
                        </div>
                    </div>

                    <button
                        onClick={() => setGameTimer(Math.min(15, gameTimer + 1))}
                        className={stepperButtonClass}
                        disabled={gameTimer >= 15}
                    >
                        +
                    </button>
                </div>
            </section>

            {/* Start Button */}
            <button
                onClick={onStartGame}
                className="mt-4 flex min-h-[52px] w-full items-center justify-center space-x-2 rounded-spy bg-spy-text px-6 py-4 text-[15px] font-semibold tracking-[0.01em] text-spy-bg hover:opacity-90"
            >
                <Play className="h-5 w-5" />
                <span>{t.startGame}</span>
            </button>
        </div>
    );
};

export default GameSetup;
